import React, { useEffect } from "react";
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonImg,
  IonItem,
  IonPage,
  IonToolbar,
} from "@ionic/react";
import DataController from "@controllers/DataController";
import { Capacitor } from "@capacitor/core";
import { EvidenceContainer, HeaderTitle } from "parafolio-components";
import { useHelpCentre } from "@hooks/app/useHelpCentre";
import _ from "lodash";
import { Button_Audio_Play_White, Button_Audio_Stop, Button_Disclosure_Right } from "@assets/images";
import { HelpCentreContent, IAccessibilitySettings } from "@interfaces/Interfaces";
import { useHistory } from "react-router";
import { useHidingTabBar } from "@hooks/tabs/useHidingTabBar";
import * as FirebaseService from "@controllers/FirebaseService";
import { useRecoilValue } from "recoil";
import { accessibilityAtom } from "@state/State";
import { useTextToSpeech } from "@hooks/accessibility/useTextToSpeech";

const HelpCentre: React.FC = () => {
  const history = useHistory();

  const accessibilitySettings = useRecoilValue<IAccessibilitySettings | null>(accessibilityAtom);
  const { handleTTS, playing, stopTTS } = useTextToSpeech("help-centre-container");

  const { helpCentreContent } = useHelpCentre();
  useHidingTabBar();

  const sections = _.uniq(helpCentreContent.filter((item) => item.Enabled && item.Section).map((item) => item.Section));

  const topSection = helpCentreContent.find((item) => item.Section === "TOP");
  const bottomSection = helpCentreContent.find((item) => item.Section === "TAIL");

  useEffect(() => {
    return () => {
      stopTTS();
    };
  }, []);

  const buttonClicked = async (subSection: HelpCentreContent): Promise<void> => {
    await FirebaseService.logEvent("help_centre_button_pressed", {
      page: subSection.Title,
      pageId: subSection.ID,
    });

    history.push(`/dashboard/help-centre/${subSection.ID}`);
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar
          mode="ios"
          className="navBar"
          style={{
            maxWidth: DataController.isWeb() ? 980 : undefined,
            height: Capacitor.getPlatform() === "android" ? "54px" : "unset",
          }}
        >
          <IonButtons slot="start">
            <IonBackButton
              className="header-back-buttons"
              defaultHref="/dashboard"
              text={DataController.getBackIconText()}
              icon={DataController.getBackIconType()}
              style={{
                marginLeft: Capacitor.getPlatform() === "android" ? 8 : 0,
                "--icon-font-size": Capacitor.getPlatform() === "android" ? "24px" : "30px",
              }}
            />
          </IonButtons>
          <HeaderTitle>{"Help Centre"}</HeaderTitle>
          <IonButtons slot="end">
            {accessibilitySettings?.textToSpeech && (
              <IonButton
                mode="ios"
                className="cancelButton"
                onClick={() => handleTTS()}
              >
                <IonImg
                  src={playing ? Button_Audio_Stop : Button_Audio_Play_White}
                  className="headerIcon"
                />
              </IonButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="page-background">
        <EvidenceContainer
          id="help-centre-container"
          style={{ paddingTop: 0 }}
        >
          {topSection && (
            <div className="flex justify-center">
              <div className="relative w-full max-w-app-column bg-white py-4 pl-safe-area-l pr-safe-area-r">
                <div className="mb-3 text-acc-20px font-acc-700 text-grey-90">{topSection.Title}</div>
                <div
                  className="text-acc-16px leading-default tracking-default text-grey-90"
                  dangerouslySetInnerHTML={{ __html: topSection.HTML }}
                />
              </div>
            </div>
          )}
          {sections.map((section, sectionIndex) => {
            const subSections = helpCentreContent.filter((item) => item.Section === section);

            return (
              <div
                key={section}
                className="flex w-full max-w-app-column flex-col justify-center self-center"
              >
                <div className="relative pl-safe-area-l pr-safe-area-r">
                  <div className="py-1 text-acc-13px font-bold text-grey-70">{section.toUpperCase()}</div>
                </div>
                <div className="w-full max-w-app-column bg-white">
                  {subSections.map((subSection, subSectionIndex) => {
                    const hideSeparator =
                      subSectionIndex === subSections.length - 1 && sectionIndex !== sections.length - 1;

                    return (
                      <IonItem
                        button
                        className={`help-centre-button flex ${hideSeparator && "help-centre-button-no-border"}`}
                        detail={false}
                        onClick={() => buttonClicked(subSection)}
                        key={subSection.ID}
                      >
                        <div className="flex min-h-[50px] flex-1 items-center justify-between pl-4">
                          <div className="leading-acc-lh acc-underline pointer-events-none text-acc-16px font-acc-600 tracking-default text-grey-90">
                            {subSection.Title}
                          </div>
                          <IonImg
                            src={Button_Disclosure_Right}
                            className="h-11 w-11"
                          />
                        </div>
                      </IonItem>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {bottomSection && (
            <div className="flex justify-center">
              <div className="relative mb-2 flex w-full max-w-app-column flex-col bg-white py-4 pl-safe-area-l pr-safe-area-r">
                <div
                  className="tailwind-anchor mt-4 text-acc-16px font-acc-600 leading-default tracking-default text-grey-90"
                  dangerouslySetInnerHTML={{ __html: bottomSection.HTML }}
                />
                <IonButton
                  mode="ios"
                  className="grey-button mt-3 h-[50px] w-full max-w-button self-center"
                  onClick={() => history.push("/dashboard/help-centre/contact-us", { from: "help-centre" })}
                >
                  <div className="acc-underline text-center text-16px font-semibold tracking-default text-cta-blue">
                    {"Contact support"}
                  </div>
                </IonButton>
              </div>
            </div>
          )}
        </EvidenceContainer>
      </IonContent>
    </IonPage>
  );
};

export default HelpCentre;
